// Settings → Plan (PR 1a): the user's plan, read-only. What's included, what costs extra,
// "Renews on the 1st", the features (not built yet = "Coming soon"), a change waiting for
// the 1st, and the summary shown when the plan was assigned, word for word (decision 126).
// Nothing shows when the account has no plan yet (admins see that; users aren't told).
// PR 2: the plan-minutes meter ("142 of 300 minutes used · resets Dec 1") and the monthly
// usage report. `reloadKey` changes on the live event readiness.changed (80 % / used up).

import { useEffect, useState } from 'react';
import { getMyPlan, getMyUsage, type MyPlan, type MyUsage } from '../api';
import { billingDayText, count, money, priceText, rateText } from '../plan-text';
import { UsageBar, UsageReport } from './UsageParts';

export function PlanSetting({ reloadKey = 0 }: { reloadKey?: number }) {
  const [data, setData] = useState<MyPlan | null>(null);
  const [usage, setUsage] = useState<MyUsage | null>(null);
  useEffect(() => {
    getMyPlan().then(setData, () => setData(null));
  }, []);
  useEffect(() => {
    getMyUsage().then(setUsage, () => undefined); // a failed read keeps what is shown
  }, [reloadKey]);
  const p = data?.plan;
  if (!p) return null;
  const t = p.terms;
  const texts = t.texts > 0 ? `${count(t.texts)} texts a month` : null;
  return (
    <>
      <h3 className="setting-group">Plan</h3>
      <div className="setting setting-stack plan-setting">
        <div className="setting-text">
          <span className="setting-name">
            {t.name} · {priceText(t)}
          </span>
          <span className="setting-help">Renews on the 1st of every month.</span>
        </div>
        <ul className="plan-facts">
          <li>{t.minutes > 0 ? `${count(t.minutes)} minutes a month, calls in and out` : 'Calls paid per minute'}</li>
          {texts && (
            <li>
              {texts} <span className="plan-soon">Coming soon</span>
            </li>
          )}
          <li>
            {t.minutes > 0 ? 'Extra minutes' : 'Calls'}: {rateText(t.overagePerMin)} a minute
          </li>
          <li>{t.firstNumberIncluded ? `1 number included · extra numbers ${money(t.numberCents)}/month` : `Numbers: ${money(t.numberCents)}/month each`}</li>
          <li>Unused minutes and texts reset on the 1st.</li>
        </ul>
        {usage?.meter && (
          <UsageBar
            used={usage.meter.used}
            bundle={usage.meter.bundle}
            left={usage.meter.left}
            level={usage.meter.level}
            resetText={usage.meter.resetText}
          />
        )}
        {data?.next && (
          <p className="setting-note plan-next" role="status">
            Your plan changes to {data.next.name} ({priceText(data.next)}) on {billingDayText(data.next.from)}.
          </p>
        )}
        {p.features.length > 0 && (
          <ul className="plan-features" aria-label="What’s included">
            {p.features.map((f) => (
              <li key={f.text}>
                {f.text}
                {f.soon && <span className="plan-soon">Coming soon</span>}
              </li>
            ))}
          </ul>
        )}
        {usage && usage.report.length > 0 && (
          <details className="plan-usage" open={false}>
            <summary>Monthly usage</summary>
            <UsageReport months={usage.report} caption="Your calls by month" />
            <span className="setting-help">Plan minutes are the minutes your calls took from your plan. Each answered call counts in whole minutes.</span>
          </details>
        )}
        {p.order && (
          <details className="plan-order">
            <summary>Your order summary</summary>
            <ul>
              {p.order.lines.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          </details>
        )}
        <span className="setting-help">To change your plan, contact support.</span>
      </div>
    </>
  );
}
